<script setup lang="ts">
import UserAvatar from "@persolog/ui/UserAvatar.vue";
import { Trainer } from "../../types/PersologOnlineTypes.ts";
import { computed, onMounted, ref, watch } from "vue";
import { LanguageManagementAPI, ProfileAdministrationAPI } from "../../api";
import { useI18n } from "vue-i18n";
import TransferDialog from "./TransferDialog.vue";
import { filenameFromTimeRange } from "../../utils/GeneralUtils.ts";
import { DataTableExportFunctionOptions } from "primevue/datatable";
import { i18n } from "../../main.ts";
import TransactionDialog from "./TransactionDialog.vue";
import StockDialog from "./StockDialog.vue";
import ReclaimDialog from "./ReclaimDialog.vue";
import { PROFILE_RECLAIM_FLAG, useFeatureFlag } from "../../composables/useFeatureFlag.ts";

const { t } = useI18n();
const profileReclaimEnabled = useFeatureFlag(PROFILE_RECLAIM_FLAG);

const props = defineProps({
    trainer: {
        type: Object as () => Trainer,
        required: true
    },
    headline: {
        type: String,
        required: true
    }
});

const showTransferDialog = ref(false);
const showTransactionDialog = ref(false);
const showStockDialog = ref(false);
const showReclaimDialog = ref(false);
const activeAccount = ref<Trainer>();
const accounts = ref<Trainer[]>([]);
const expandedRows = ref<Trainer[]>([]);
const texts = ref<Record<string, string>>({});
const loading = ref(false);
const totalRecords = ref(0);
const pageSize = ref(10);
const search = ref("");
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function transfer(account: Trainer) {
    showTransferDialog.value = true;
    activeAccount.value = account;
}

function reclaim(account: Trainer) {
    showReclaimDialog.value = true;
    activeAccount.value = account;
}

function stock(account: Trainer) {
    showStockDialog.value = true;
    activeAccount.value = account;
}

function transaction(account: Trainer) {
    showTransactionDialog.value = true;
    activeAccount.value = account;
}

async function updatePurchaseLimit(id: string, value: boolean) {
    const index = accounts.value.findIndex((account) => account.id === id);
    await ProfileAdministrationAPI.setMonthlyBilling(id, value);
    if (index !== -1) {
        const newLimit = value ? 500 : 0;
        accounts.value[index] = { ...accounts.value[index], purchaseLimit: newLimit };
    }
}

async function fetchPage(page: number) {
    loading.value = true;
    try {
        const response = await ProfileAdministrationAPI.getPagedSubAccounts({
            trainerId: props.trainer.id,
            page,
            size: pageSize.value,
            search: search.value || undefined,
        });
        accounts.value = response.trainers;
        totalRecords.value = response.totalElements;
    } finally {
        loading.value = false;
    }
}

function onPage(event: { page: number; rows: number }) {
    pageSize.value = event.rows;
    fetchPage(event.page);
}

function onSearchInput() {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => fetchPage(0), 300);
}

const dt = ref();

onMounted(async () => {
    const [textsResult] = await Promise.all([
        LanguageManagementAPI.fetchTexts("products"),
        fetchPage(0),
    ]);

    texts.value = Object.fromEntries(
        textsResult.map((id) => [
            id.identifier,
            id.texts.find((t) => t.language === i18n.global.locale.value)?.text ??
            id.texts.find((t) => t.language === "en")?.text ??
            id.identifier
        ])
    );
});

watch(() => props.trainer.id, () => {
    search.value = "";
    fetchPage(0);
});

const exportfilename = computed(() => filenameFromTimeRange(props.headline, undefined, undefined));
const prepareExport = (options: DataTableExportFunctionOptions) => {
    if (options.field === "purchaseLimit") {
        return Number(options.data) > 0 ? t("common.yes") : t("common.no");
    }
    return String(options.data);
};
const exportCSV = () => dt.value.exportCSV();
</script>

<template>
    <DataTable
        id="sub-account-table_data-table_table"
        :value="accounts"
        ref="dt"
        v-model:expandedRows="expandedRows"
        :lazy="true"
        :loading="loading"
        :paginator="totalRecords > 0"
        :rows="pageSize"
        :rows-per-page-options="[10, 25, 50]"
        :total-records="totalRecords"
        @page="onPage"
        :style="{ minWidth: '100%' }"
        :export-filename="exportfilename"
        :export-function="prepareExport"
    >
        <template #header>
            <div class="flex items-center" :class="headline ? 'justify-start' : 'justify-end'">
                <h3 v-if="headline" class="my-0 mr-6">{{ headline }}</h3>
                <InputText
                    id="sub-account-table_input_search"
                    v-model="search"
                    :placeholder="t('fields.searchPlaceholderSubaccounts')"
                    @input="onSearchInput"
                    class="w-5/12"
                />
            </div>
        </template>

        <Column expander style="width: 2rem" />
        <Column field="portrait" :exportable="false">
            <template #body="slotProps">
                <UserAvatar :image-url="slotProps.data.portrait" :trainer="slotProps.data" />
            </template>
        </Column>
        <Column field="firstname" :header="t('trainer-settings.firstname.label')" />
        <Column field="lastname" :header="t('trainer-settings.lastname.label')" />
        <Column field="email" :header="t('trainer-settings.email.label')" />
        <Column field="purchaseLimit" :header="t('trainer-settings.billing.label')">
            <template #body="slotProps">
                <ToggleSwitch
                    :model-value="slotProps.data.purchaseLimit > 0"
                    @update:model-value="(value: boolean) => updatePurchaseLimit(slotProps.data.id, value)"
                />
            </template>
        </Column>
        <Column :header="t('header.accountSettings.accountLinks.actions')">
            <template #body="slotProps">
                <Button
                    class="mr-2"
                    :id="'sub-account-table_btn_transfer_' + slotProps.data.id"
                    icon="pi pi-file-export"
                    v-tooltip="t('buttons.transfer')"
                    rounded
                    outlined
                    @click="transfer(slotProps.data)"
                ></Button>
                <Button
                    v-if="profileReclaimEnabled"
                    class="mr-2"
                    :id="'sub-account-table_btn_reclaim_' + slotProps.data.id"
                    icon="pi pi-history"
                    v-tooltip="t('header.accountSettings.accountLinks.tooltips.reclaim')"
                    rounded
                    outlined
                    @click="reclaim(slotProps.data)"
                ></Button>
                <Button
                    class="mr-2"
                    :id="'sub-account-table_btn_stock_view_' + slotProps.data.id"
                    icon="pi pi-warehouse"
                    v-tooltip="t('header.accountSettings.purchases')"
                    rounded
                    outlined
                    @click="stock(slotProps.data)"
                ></Button>
                <Button
                    :id="'sub-account-table_btn_transactions_' + slotProps.data.id"
                    icon="pi pi-info-circle"
                    v-tooltip="t('header.accountSettings.transactions')"
                    rounded
                    outlined
                    @click="transaction(slotProps.data)"
                ></Button>
            </template>
        </Column>
        <template #expansion="slotProps">
            <Card class="mb-2">
                <template #title>
                    <div class="flex items-center gap-2">
                        <UserAvatar :trainer="slotProps.data" :image-url="slotProps.data.portrait" size="large" />
                        {{
                            t("header.accountSettings.details")
                        }}
                    </div>
                </template>
                <template #content>
                    <div>
                        <span class="font-bold">{{ t("trainer-settings.company.label") }}:</span>
                        {{ slotProps.data.company === "" ? t("fields.empty") : slotProps.data.company }}
                    </div>
                    <div v-if="slotProps.data.address" class="font-bold mt-2">
                        {{ t("header.accountSettings.address") }}:
                    </div>
                    <div v-if="slotProps.data.address">{{ slotProps.data.address.street }}</div>
                    <div v-if="slotProps.data.address">
                        {{ slotProps.data.address.postalCode }} {{ slotProps.data.address.city }}
                    </div>
                    <div v-if="slotProps.data.address">{{ slotProps.data.address.country }}</div>
                    <div v-if="slotProps.data.address">{{ slotProps.data.address.website }}</div>
                    <div v-if="slotProps.data.address">{{ slotProps.data.address.phoneNo }}</div>
                </template>
            </Card>
        </template>

        <template #paginatorstart> &nbsp;</template>
        <template #paginatorend>
            <Button
                icon="pi pi-download"
                text
                :label="t('buttons.export')"
                size="small"
                @click="exportCSV()"
                :disabled="accounts.length == 0"
            />
        </template>
    </DataTable>
    <TransferDialog
        v-if="activeAccount && showTransferDialog"
        :visible-toggle="showTransferDialog"
        :account="activeAccount"
        :trainer="props.trainer"
        @update:visible="(visible) => (showTransferDialog = visible)"
    />
    <ReclaimDialog
        v-if="profileReclaimEnabled && activeAccount && showReclaimDialog"
        :visible-toggle="showReclaimDialog"
        :account="activeAccount"
        :trainer="props.trainer"
        @update:visible="(visible) => (showReclaimDialog = visible)"
    />
    <TransactionDialog
        v-if="activeAccount && showTransactionDialog"
        :visible-toggle="showTransactionDialog"
        :texts="texts"
        :account="activeAccount"
        :trainer="props.trainer"
        @update:visible="(visible) => (showTransactionDialog = visible)"
    />
    <StockDialog
        v-if="activeAccount && showStockDialog"
        :visible-toggle="showStockDialog"
        :texts="texts"
        :account="activeAccount"
        @update:visible="(visible) => (showStockDialog = visible)"
    />
</template>
